import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from './global-environment';
import { Environment, CustomerUser, ModuleConfig, Company } from '../models/customer-data.model';

@Injectable({
  providedIn: 'root'
})
export class CustomerManagerService {

  private get apiUrl(): string {
    return environment.apiUrl || 'https://quallicoapi-jp.azurewebsites.net';
  }

  constructor(private http: HttpClient) {}

  // ==================== 環境管理 API ====================

  /**
   * 取得所有環境配置
   */
  getEnvironments(searchTerm?: string, page: number = 1, pageSize: number = 10): Observable<{ data: Environment[], total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm && searchTerm.trim()) {
      params = params.set('search', searchTerm.trim());
    }

    return this.http.get<{ data: Environment[], total: number }>(`${this.apiUrl}/api/customer-manager/environments`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 根據 ID 取得單一環境配置
   */
  getEnvironmentById(id: string): Observable<Environment> {
    return this.http.get<Environment>(`${this.apiUrl}/api/customer-manager/environments/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 新增環境配置
   */
  createEnvironment(environment: Omit<Environment, 'id' | 'createdAt' | 'updatedAt'>): Observable<Environment> {
    return this.http.post<Environment>(`${this.apiUrl}/api/customer-manager/environments`, environment)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 更新環境配置
   */
  updateEnvironment(id: string, environment: Partial<Environment>): Observable<Environment> {
    return this.http.put<Environment>(`${this.apiUrl}/api/customer-manager/environments/${id}`, environment)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 刪除環境配置
   */
  deleteEnvironment(id: string, companyid: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/customer-manager/environments/${companyid}/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // ==================== 客戶使用者管理 API ====================

  /**
   * 取得所有客戶使用者
   */
  getCustomerUsers(searchTerm?: string, companyid?: string, page: number = 1, pageSize: number = 10): Observable<{ data: CustomerUser[], total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm && searchTerm.trim()) {
      params = params.set('search', searchTerm.trim());
    }

    if (companyid && companyid.trim()) {
      params = params.set('companyid', companyid.trim());
    }

    return this.http.get<{ data: CustomerUser[], total: number }>(`${this.apiUrl}/api/customer-manager/users`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 根據 ID 取得單一客戶使用者
   */
  getCustomerUserById(id: string): Observable<CustomerUser> {
    return this.http.get<CustomerUser>(`${this.apiUrl}/api/customer-manager/users/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 新增客戶使用者
   */
  createCustomerUser(user: Omit<CustomerUser, 'id' | 'createdAt' | 'updatedAt'>): Observable<CustomerUser> {
    return this.http.post<CustomerUser>(`${this.apiUrl}/api/customer-manager/users`, user)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 更新客戶使用者
   */
  updateCustomerUser(id: string, user: Partial<CustomerUser>): Observable<CustomerUser> {
    return this.http.put<CustomerUser>(`${this.apiUrl}/api/customer-manager/users/${id}`, user)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 刪除客戶使用者
   */
  deleteCustomerUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/api/customer-manager/users/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // ==================== 模組管理 API ====================

  /**
   * 取得所有模組配置
   */
  getModuleConfigs(searchTerm?: string, page: number = 1, pageSize: number = 10): Observable<{ data: ModuleConfig[], total: number }> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (searchTerm && searchTerm.trim()) {
      params = params.set('search', searchTerm.trim());
    }

    return this.http.get<{ data: ModuleConfig[], total: number }>(`${this.apiUrl}/api/customer-manager/modules`, { params })
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 根據 ID 取得單一模組配置
   */
  getModuleConfigById(id: string): Observable<ModuleConfig> {
    return this.http.get<ModuleConfig>(`${this.apiUrl}/api/customer-manager/modules/${id}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 根據公司 ID 取得模組配置
   */
  getModuleConfigByCompanyId(companyId: string): Observable<ModuleConfig> {
    return this.http.get<ModuleConfig>(`${this.apiUrl}/api/customer-manager/modules/company/${companyId}`)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * 更新模組配置
   */
  updateModuleConfig(id: string, moduleConfig: Partial<ModuleConfig>): Observable<ModuleConfig> {
    return this.http.put<ModuleConfig>(`${this.apiUrl}/api/customer-manager/modules/${id}`, moduleConfig)
      .pipe(
        catchError(this.handleError)
      );
  }

  // ==================== AI 助手管理 API ====================

  /**
   * 取得特定公司的所有AI助手資訊
   */
  getCompanyAssistants(companyid: string): Observable<{ assistantNo: string; title: string }[]> {
    let params = new HttpParams().set('companyid', companyid);

    return this.http.get<{ success: boolean; data: { assistantNo: string; title: string }[]; count: number }>(`${this.apiUrl}/api/customer-manager/assistants`, { params })
      .pipe(
        map(response => response.data || []),
        catchError(this.handleError)
      );
  }

  /**
   * 更新客戶可用的 AI assistants
   */
  updateAssistants(companyid: string, assistantNumbers: string[]): Observable<any> {
    const payload = {
      companyid,
      assistantNumbers
    };

    return this.http.put<any>(`${this.apiUrl}/api/modules/updateAssistants`, payload)
      .pipe(
        catchError(this.handleError)
      );
  }

  // ==================== 公司管理 API ====================

  /**
   * 取得所有公司列表（包含使用者數量）
   */
  getCompanies(): Observable<Company[]> {
    return this.http.get<{ success: boolean; data: Company[]; count: number; requestId: string; processingTime: number }>(`${this.apiUrl}/api/customer-manager/companies`)
      .pipe(
        map(response => {
          if (response.success && response.data) {
            console.log(`成功載入 ${response.count} 家公司資料，處理時間: ${response.processingTime}ms`);
            return response.data;
          } else {
            console.warn('API 回應格式異常:', response);
            return [];
          }
        }),
        catchError(this.handleError)
      );
  }

  /**
   * 取得公司的使用者統計資料
   */
  getCompanyUserStats(): Observable<{ [companyId: string]: number }> {
    return this.http.get<{ [companyId: string]: number }>(`${this.apiUrl}/api/customer-manager/companies/user-stats`)
      .pipe(
        catchError(this.handleError)
      );
  }

  // ==================== 錯誤處理 ====================

  /**
   * HTTP 錯誤處理
   */
  private handleError(error: any): Observable<never> {
    console.error('CustomerManagerService 錯誤:', error);

    let errorMessage = '發生未知錯誤';

    if (error.error instanceof ErrorEvent) {
      // 客戶端錯誤
      errorMessage = `錯誤: ${error.error.message}`;
    } else {
      // 伺服器端錯誤
      switch (error.status) {
        case 400:
          errorMessage = '請求參數錯誤';
          break;
        case 401:
          errorMessage = '未授權，請重新登入';
          break;
        case 403:
          errorMessage = '權限不足';
          break;
        case 404:
          errorMessage = '資源不存在';
          break;
        case 500:
          errorMessage = '伺服器內部錯誤';
          break;
        default:
          errorMessage = `錯誤代碼: ${error.status}\n訊息: ${error.message}`;
      }
    }

    return throwError(() => errorMessage);
  }
}
